import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import {
  ArrowLeft, ArrowRight, Check, FileSpreadsheet, Search, Filter,
  Download, Loader2, X, Eye, Package, DollarSign, Image as ImageIcon,
  CheckCircle, AlertCircle, XCircle, Sparkles, ChevronLeft, ChevronRight,
  Pause, Play, StopCircle
} from 'lucide-react';
import { AdminLayout } from '../components/admin/AdminLayout';
import { useAdminTheme } from '../contexts/AdminThemeContext';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { id: 1, name: 'Scanner', icon: FileSpreadsheet, description: 'Coller l\'URL du spreadsheet' },
  { id: 2, name: 'Onglet', icon: Search, description: 'Choisir l\'onglet' },
  { id: 3, name: 'Filtres', icon: Filter, description: 'Filtrer les produits' },
  { id: 4, name: 'Prévisualisation', icon: Eye, description: 'Sélectionner les produits' },
  { id: 5, name: 'Import', icon: Download, description: 'Importer' },
  { id: 6, name: 'Validation', icon: CheckCircle, description: 'Vérifier les photos' }
];

// Limite d'affichage pour la prévisualisation
const PREVIEW_PAGE_SIZE = 200;

export function AdminSpreadsheetImportPage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState('');
  
  // Step 1 data
  const [spreadsheetUrl, setSpreadsheetUrl] = useState('');
  const [spreadsheetId, setSpreadsheetId] = useState('');
  const [tabs, setTabs] = useState([]);
  
  // Step 2 data
  const [selectedTab, setSelectedTab] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  
  // Step 3 data
  const [filterKeyword, setFilterKeyword] = useState('');
  const [filterMaxPrice, setFilterMaxPrice] = useState('');
  const [filterLimit, setFilterLimit] = useState(200);
  
  // Step 4 data - Pagination
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [previewPage, setPreviewPage] = useState(0);
  const parentRef = useRef(null);
  
  // Step 5 data
  const [importOptions, setImportOptions] = useState({
    generate_ai: false,
    as_draft: true,
    margin_rule: 2.5
  });
  const [importJobId, setImportJobId] = useState(null);
  const [importProgress, setImportProgress] = useState(null);
  const [importResults, setImportResults] = useState(null);
  const pollingRef = useRef(null);
  
  // Scan progress state
  const [scanProgress, setScanProgress] = useState(null); // { step: string, current: number, total: number }
  
  // Photo validation state (étape 6)
  const [photoValidation, setPhotoValidation] = useState({
    currentProductIndex: 0,
    productsToValidate: [],
    validatedProducts: [],
    customUrls: ''
  });
  const [validationLoading, setValidationLoading] = useState(false);
  
  // Column mapping configuration
  const [columnConfig, setColumnConfig] = useState({
    preset: 'acbuy_standard', // 'acbuy_standard', 'single_product', 'custom'
    // Structure: 2 produits par ligne (ACBUY standard)
    product1: {
      name: 'A',
      link: 'C',
      priceUsd: 'D',
      priceCny: 'E'
    },
    product2: {
      enabled: true,
      name: 'F',
      link: 'H',
      priceUsd: 'I',
      priceCny: 'J'
    }
  });
  const [showColumnConfig, setShowColumnConfig] = useState(false);

  useEffect(() => {
    const storedToken = localStorage.getItem('admin_token');
    if (storedToken) {
      setToken(storedToken);
    } else {
      navigate('/admin');
    }
  }, [navigate]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, []);

  // Pagination logic pour la prévisualisation
  const totalPreviewPages = Math.ceil(filteredProducts.length / PREVIEW_PAGE_SIZE);
  const paginatedProducts = filteredProducts.slice(
    previewPage * PREVIEW_PAGE_SIZE,
    (previewPage + 1) * PREVIEW_PAGE_SIZE
  );

  // Virtualizer pour la liste des produits
  const rowVirtualizer = useVirtualizer({
    count: paginatedProducts.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 80,
    overscan: 5,
  });

  // Step 1: Scan spreadsheet with progress indicators
  const handleScan = async () => {
    if (!spreadsheetUrl.trim()) {
      toast.error('Entrez une URL de spreadsheet');
      return;
    }
    
    setLoading(true);
    setScanProgress({ step: '📡 Connexion au spreadsheet...', current: 0, total: 10 });
    
    try {
      setScanProgress({ step: '🔍 Lecture des onglets...', current: 2, total: 10 });
      
      const res = await fetch(`${API_URL}/api/sourcing/spreadsheet/scan`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ url: spreadsheetUrl })
      });
      
      setScanProgress({ step: '📊 Comptage des produits...', current: 7, total: 10 });
      
      const data = await res.json();
      
      if (!res.ok) {
        toast.error(data.detail || 'Erreur de scan');
        return;
      }
      
      setScanProgress({ step: `✅ ${data.tabs?.length || 0} onglet(s) trouvé(s)`, current: 10, total: 10 });
      
      setSpreadsheetId(data.spreadsheet_id);
      setTabs(data.tabs || []);
      toast.success(`${data.tabs?.length || 0} onglet(s) trouvé(s)`);
      setCurrentStep(2);
    } catch (err) {
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
      setScanProgress(null);
    }
  };

  // Step 2: Select tab and fetch products with progress indicators
  const handleSelectTab = async (tab) => {
    setSelectedTab(tab);
    setLoading(true);
    setScanProgress({ step: `🔍 Lecture de l'onglet ${tab.icon} ${tab.name}...`, current: 0, total: 10 });
    
    try {
      setScanProgress({ step: `📊 Chargement des produits...`, current: 3, total: 10 });
      
      const res = await fetch(`${API_URL}/api/sourcing/spreadsheet/fetch-tab`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          spreadsheet_id: spreadsheetId,
          gid: tab.gid,
          sheet_name: tab.sheet_name || tab.name, // Utiliser le nom de l'onglet si disponible
          limit: 2000, // Récupérer jusqu'à 2000 produits
          column_config: columnConfig // Configuration des colonnes
        })
      });
      
      setScanProgress({ step: `📦 Analyse des produits...`, current: 7, total: 10 });
      
      const data = await res.json();
      
      if (!res.ok) {
        toast.error(data.detail || 'Erreur de récupération');
        return;
      }
      
      setAllProducts(data.products || []);
      
      // Compter les produits avec images
      const withImages = (data.products || []).filter(p => p.image_url).length;
      setScanProgress({ step: `✅ ${data.total} produits trouvés`, current: 10, total: 10 });
      toast.success(`${data.total} produit(s) trouvé(s) (${withImages} avec images)`);
      setCurrentStep(3);
    } catch (err) {
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
      setScanProgress(null);
    }
  };

  // Step 3: Apply filters
  const handleApplyFilters = () => {
    let filtered = [...allProducts];
    
    // Filter by keyword
    if (filterKeyword.trim()) {
      const keyword = filterKeyword.toLowerCase();
      filtered = filtered.filter(p => 
        p.name?.toLowerCase().includes(keyword)
      );
    }
    
    // Filter by max price (USD)
    if (filterMaxPrice) {
      const maxPrice = parseFloat(filterMaxPrice);
      filtered = filtered.filter(p => 
        (p.price_usd || 0) <= maxPrice
      );
    }
    
    // Apply limit
    if (filterLimit > 0 && filterLimit < filtered.length) {
      filtered = filtered.slice(0, filterLimit);
    }
    
    setFilteredProducts(filtered);
    setSelectedProducts(new Set(filtered.map((_, idx) => idx)));
    setPreviewPage(0);
    
    const withImages = filtered.filter(p => p.image_url).length;
    toast.success(`${filtered.length} produit(s) après filtrage (${withImages} avec images)`);
    setCurrentStep(4);
  };

  // Step 4: Toggle product selection
  const toggleProduct = (globalIndex) => {
    const newSelected = new Set(selectedProducts);
    if (newSelected.has(globalIndex)) {
      newSelected.delete(globalIndex);
    } else {
      newSelected.add(globalIndex);
    }
    setSelectedProducts(newSelected);
  };

  const toggleAll = () => {
    if (selectedProducts.size === filteredProducts.length) {
      setSelectedProducts(new Set());
    } else {
      setSelectedProducts(new Set(filteredProducts.map((_, idx) => idx)));
    }
  };

  const toggleCurrentPage = () => {
    const newSelected = new Set(selectedProducts);
    const startIdx = previewPage * PREVIEW_PAGE_SIZE;
    const endIdx = Math.min(startIdx + PREVIEW_PAGE_SIZE, filteredProducts.length);
    
    // Check if all on current page are selected
    let allSelected = true;
    for (let i = startIdx; i < endIdx; i++) {
      if (!newSelected.has(i)) {
        allSelected = false;
        break;
      }
    }
    
    if (allSelected) {
      // Deselect all on current page
      for (let i = startIdx; i < endIdx; i++) {
        newSelected.delete(i);
      }
    } else {
      // Select all on current page
      for (let i = startIdx; i < endIdx; i++) {
        newSelected.add(i);
      }
    }
    
    setSelectedProducts(newSelected);
  };

  // Step 5: Import with background job
  const handleImport = async () => {
    const productsToImport = filteredProducts.filter((_, idx) => selectedProducts.has(idx));
    
    if (productsToImport.length === 0) {
      toast.error('Sélectionnez au moins un produit');
      return;
    }
    
    if (productsToImport.length > 200) {
      toast.error('Maximum 200 produits par import');
      return;
    }
    
    setLoading(true);
    
    try {
      // Démarrer l'import en arrière-plan
      const res = await fetch(`${API_URL}/api/sourcing/spreadsheet/start-import`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          spreadsheet_id: spreadsheetId,
          products: productsToImport,
          options: {
            generate_ai_description: importOptions.generate_ai,
            as_draft: importOptions.as_draft,
            margin_rule: importOptions.margin_rule
          }
        })
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        toast.error(data.detail || 'Erreur de démarrage');
        setLoading(false);
        return;
      }
      
      // Job créé, démarrer le polling
      setImportJobId(data.job_id);
      setImportProgress({
        status: 'pending',
        total: data.total,
        processed: 0,
        imported: 0,
        duplicates: 0,
        errors: 0,
        current_product: null,
        logs: []
      });
      
      toast.success('Import démarré en arrière-plan');
      
      // Polling toutes les 2 secondes
      pollingRef.current = setInterval(() => {
        pollJobStatus(data.job_id);
      }, 2000);
      
    } catch (err) {
      toast.error('Erreur de connexion');
      setLoading(false);
    }
  };

  const pollJobStatus = useCallback(async (jobId) => {
    try {
      const res = await fetch(`${API_URL}/api/sourcing/spreadsheet/job/${jobId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!res.ok) {
        return;
      }
      
      const data = await res.json();
      
      setImportProgress(data);
      
      // Si terminé, arrêter le polling
      if (data.status === 'completed' || data.status === 'failed') {
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
        
        setImportResults({
          imported: data.imported,
          duplicates: data.duplicates,
          errors: data.errors,
          details: data.logs,
          stats: data.stats // Stats des images
        });
        
        setLoading(false);
        
        if (data.status === 'completed') {
          if (data.imported > 0) {
            toast.success(`${data.imported} produit(s) importé(s) avec succès !`);
          }
          if (data.duplicates > 0) {
            toast.warning(`${data.duplicates} doublon(s) ignoré(s)`);
          }
        } else {
          toast.error('Import échoué');
        }
      }
    } catch (err) {
      console.error('Polling error:', err);
    }
  }, [token]);

  // Calculate selling price
  const calculateSellingPrice = (priceCny, priceUsd) => {
    const sourceCny = priceCny > 0 ? priceCny : (priceUsd * 7.2);
    return (sourceCny * importOptions.margin_rule).toFixed(2);
  };

  // Import control functions: Pause, Resume, Cancel
  const handlePauseImport = async () => {
    if (!importJobId) return;
    
    try {
      const res = await fetch(`${API_URL}/api/sourcing/spreadsheet/job/${importJobId}/pause`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setImportProgress(prev => ({ ...prev, status: 'paused' }));
        toast.info('⏸️ Import mis en pause');
      }
    } catch (err) {
      toast.error('Erreur lors de la pause');
    }
  };

  const handleResumeImport = async () => {
    if (!importJobId) return;
    
    try {
      const res = await fetch(`${API_URL}/api/sourcing/spreadsheet/job/${importJobId}/resume`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setImportProgress(prev => ({ ...prev, status: 'processing' }));
        toast.info('▶️ Import repris');
      }
    } catch (err) {
      toast.error('Erreur lors de la reprise');
    }
  };

  const handleCancelImport = async () => {
    if (!importJobId) return;
    
    if (!confirm('Annuler l\'import ? Les produits déjà importés resteront dans le catalogue.')) {
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/sourcing/spreadsheet/job/${importJobId}/cancel`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setImportProgress(prev => ({ ...prev, status: 'cancelled' }));
        toast.warning(`🛑 Import annulé - ${data.job?.processed || 0} produits importés`);
        
        // Arrêter le polling
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
        setLoading(false);
      }
    } catch (err) {
      toast.error('Erreur lors de l\'annulation');
    }
  };

  // Photo Validation Functions (Étape 6)
  const handleValidatePhotos = (productId) => {
    // Marquer le produit comme validé
    setPhotoValidation(prev => ({
      ...prev,
      validatedProducts: [...prev.validatedProducts, productId],
      currentProductIndex: prev.currentProductIndex + 1
    }));
    toast.success('Photos validées ✅');
  };

  const handleRecapturePhotos = async (productId) => {
    setValidationLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/sourcing/products/${productId}/recapture-photos`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        toast.success(`📸 ${data.photo_count} photos recapturées`);
        // Mettre à jour l'affichage
      } else {
        toast.error(data.detail || 'Erreur de recapture');
      }
    } catch (err) {
      toast.error('Erreur de connexion');
    } finally {
      setValidationLoading(false);
    }
  };

  const handleAddCustomUrls = async (productId) => {
    const urls = photoValidation.customUrls.split('\n').filter(u => u.trim());
    
    if (urls.length === 0) {
      toast.error('Entrez au moins une URL');
      return;
    }
    
    setValidationLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/sourcing/products/${productId}/add-images`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ image_urls: urls })
      });
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        toast.success(`✅ ${data.added_count} images ajoutées`);
        setPhotoValidation(prev => ({ ...prev, customUrls: '' }));
      } else {
        toast.error(data.detail || 'Erreur d\'ajout');
      }
    } catch (err) {
      toast.error('Erreur de connexion');
    } finally {
      setValidationLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="space-y-2">
              <Label className="text-white">URL du Google Spreadsheet</Label>
              <Input
                placeholder="https://docs.google.com/spreadsheets/d/..."
                value={spreadsheetUrl}
                onChange={(e) => setSpreadsheetUrl(e.target.value)}
                className="bg-zinc-800 border-zinc-700 text-white"
                data-testid="spreadsheet-url-input"
              />
            </div>
            
            <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <p className="text-sm text-blue-200">
                <strong>Spreadsheet ACBUY communautaire :</strong>
              </p>
              <p className="text-xs text-gray-400 mt-2 break-all">
                https://docs.google.com/spreadsheets/d/11sDC-i0wHzKKArCidG_pFxIzlJxPbfgszjDZ9jTv6Ao/edit
              </p>
              <Button 
                variant="link" 
                size="sm" 
                className="text-blue-400 p-0 h-auto mt-2"
                onClick={() => setSpreadsheetUrl('https://docs.google.com/spreadsheets/d/11sDC-i0wHzKKArCidG_pFxIzlJxPbfgszjDZ9jTv6Ao/edit')}
              >
                Utiliser ce spreadsheet
              </Button>
            </div>
            
            <Button 
              onClick={handleScan} 
              disabled={loading || !spreadsheetUrl.trim()}
              className="w-full bg-primary"
              data-testid="scan-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
              Scanner le Spreadsheet
            </Button>
            
            {/* Indicateur de progression du scan */}
            {scanProgress && (
              <div className="p-4 bg-zinc-800/70 rounded-lg border border-zinc-700 animate-pulse">
                <div className="flex items-center gap-3">
                  <Loader2 className="w-5 h-5 animate-spin text-primary" />
                  <div className="flex-1">
                    <p className="text-white font-medium">{scanProgress.step}</p>
                    <div className="w-full bg-zinc-700 rounded-full h-2 mt-2">
                      <div 
                        className="bg-primary h-2 rounded-full transition-all duration-500"
                        style={{ width: `${(scanProgress.current / scanProgress.total) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
        
      case 2:
        return (
          <div className="space-y-6">
            {/* Configuration des colonnes */}
            <div className="p-4 bg-zinc-800/50 rounded-lg border border-zinc-700">
              <div className="flex items-center justify-between mb-3">
                <Label className="text-white font-semibold">Configuration des colonnes</Label>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setShowColumnConfig(!showColumnConfig)}
                  className="text-gray-400 hover:text-white"
                >
                  {showColumnConfig ? 'Masquer' : 'Personnaliser'}
                </Button>
              </div>
              
              {/* Presets */}
              <div className="flex gap-2 flex-wrap mb-4">
                <Button
                  variant={columnConfig.preset === 'acbuy_standard' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setColumnConfig({
                    preset: 'acbuy_standard',
                    product1: { name: 'A', link: 'C', priceUsd: 'D', priceCny: 'E' },
                    product2: { enabled: true, name: 'F', link: 'H', priceUsd: 'I', priceCny: 'J' }
                  })}
                  className={columnConfig.preset === 'acbuy_standard' ? 'bg-primary' : ''}
                >
                  ACBUY Standard (2 produits/ligne)
                </Button>
                <Button
                  variant={columnConfig.preset === 'single_product' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setColumnConfig({
                    preset: 'single_product',
                    product1: { name: 'A', link: 'B', priceUsd: 'C', priceCny: 'D' },
                    product2: { enabled: false, name: '', link: '', priceUsd: '', priceCny: '' }
                  })}
                  className={columnConfig.preset === 'single_product' ? 'bg-primary' : ''}
                >
                  1 produit/ligne (A-B-C-D)
                </Button>
                <Button
                  variant={columnConfig.preset === 'custom' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => {
                    setColumnConfig(prev => ({ ...prev, preset: 'custom' }));
                    setShowColumnConfig(true);
                  }}
                  className={columnConfig.preset === 'custom' ? 'bg-primary' : ''}
                >
                  Personnalisé
                </Button>
              </div>
              
              {/* Configuration personnalisée */}
              {showColumnConfig && (
                <div className="space-y-4 pt-4 border-t border-zinc-600">
                  {/* Produit 1 */}
                  <div>
                    <p className="text-sm text-white mb-2 font-medium">Produit 1 (obligatoire)</p>
                    <div className="grid grid-cols-4 gap-2">
                      <div>
                        <Label className="text-xs text-gray-400">Nom</Label>
                        <Input
                          value={columnConfig.product1.name}
                          onChange={(e) => setColumnConfig(prev => ({
                            ...prev,
                            preset: 'custom',
                            product1: { ...prev.product1, name: e.target.value.toUpperCase() }
                          }))}
                          placeholder="A"
                          maxLength={2}
                          className="bg-zinc-700 border-zinc-600 text-white text-center"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-400">Lien ACBUY</Label>
                        <Input
                          value={columnConfig.product1.link}
                          onChange={(e) => setColumnConfig(prev => ({
                            ...prev,
                            preset: 'custom',
                            product1: { ...prev.product1, link: e.target.value.toUpperCase() }
                          }))}
                          placeholder="C"
                          maxLength={2}
                          className="bg-zinc-700 border-zinc-600 text-white text-center"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-400">Prix USD</Label>
                        <Input
                          value={columnConfig.product1.priceUsd}
                          onChange={(e) => setColumnConfig(prev => ({
                            ...prev,
                            preset: 'custom',
                            product1: { ...prev.product1, priceUsd: e.target.value.toUpperCase() }
                          }))}
                          placeholder="D"
                          maxLength={2}
                          className="bg-zinc-700 border-zinc-600 text-white text-center"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-400">Prix CNY</Label>
                        <Input
                          value={columnConfig.product1.priceCny}
                          onChange={(e) => setColumnConfig(prev => ({
                            ...prev,
                            preset: 'custom',
                            product1: { ...prev.product1, priceCny: e.target.value.toUpperCase() }
                          }))}
                          placeholder="E"
                          maxLength={2}
                          className="bg-zinc-700 border-zinc-600 text-white text-center"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Produit 2 (optionnel) */}
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Checkbox
                        checked={columnConfig.product2.enabled}
                        onCheckedChange={(checked) => setColumnConfig(prev => ({
                          ...prev,
                          preset: 'custom',
                          product2: { ...prev.product2, enabled: checked }
                        }))}
                      />
                      <Label className="text-sm text-white font-medium">Produit 2 (2ème produit par ligne)</Label>
                    </div>
                    {columnConfig.product2.enabled && (
                      <div className="grid grid-cols-4 gap-2">
                        <div>
                          <Label className="text-xs text-gray-400">Nom</Label>
                          <Input
                            value={columnConfig.product2.name}
                            onChange={(e) => setColumnConfig(prev => ({
                              ...prev,
                              preset: 'custom',
                              product2: { ...prev.product2, name: e.target.value.toUpperCase() }
                            }))}
                            placeholder="F"
                            maxLength={2}
                            className="bg-zinc-700 border-zinc-600 text-white text-center"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-gray-400">Lien ACBUY</Label>
                          <Input
                            value={columnConfig.product2.link}
                            onChange={(e) => setColumnConfig(prev => ({
                              ...prev,
                              preset: 'custom',
                              product2: { ...prev.product2, link: e.target.value.toUpperCase() }
                            }))}
                            placeholder="H"
                            maxLength={2}
                            className="bg-zinc-700 border-zinc-600 text-white text-center"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-gray-400">Prix USD</Label>
                          <Input
                            value={columnConfig.product2.priceUsd}
                            onChange={(e) => setColumnConfig(prev => ({
                              ...prev,
                              preset: 'custom',
                              product2: { ...prev.product2, priceUsd: e.target.value.toUpperCase() }
                            }))}
                            placeholder="I"
                            maxLength={2}
                            className="bg-zinc-700 border-zinc-600 text-white text-center"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-gray-400">Prix CNY</Label>
                          <Input
                            value={columnConfig.product2.priceCny}
                            onChange={(e) => setColumnConfig(prev => ({
                              ...prev,
                              preset: 'custom',
                              product2: { ...prev.product2, priceCny: e.target.value.toUpperCase() }
                            }))}
                            placeholder="J"
                            maxLength={2}
                            className="bg-zinc-700 border-zinc-600 text-white text-center"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            <div className="space-y-2">
              <Label className="text-white">Choisir un onglet ({tabs.length} disponibles)</Label>
              <div className="grid gap-2 max-h-[300px] overflow-y-auto">
                {tabs.map((tab, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleSelectTab(tab)}
                    disabled={loading}
                    data-testid={`tab-${tab.gid}`}
                    className={`p-4 rounded-lg border text-left transition-all ${
                      selectedTab?.gid === tab.gid 
                        ? 'border-primary bg-primary/10' 
                        : 'border-zinc-700 bg-zinc-800/50 hover:border-zinc-600'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-white">
                        {tab.icon} {tab.name}
                      </span>
                      <Badge variant="secondary" className="bg-zinc-700">
                        {tab.product_count || tab.estimated_products || 0} produits
                      </Badge>
                    </div>
                  </button>
                ))}
              </div>
            </div>
            
            <Button variant="outline" onClick={() => setCurrentStep(1)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
            
            {/* Indicateur de progression pour la sélection d'onglet */}
            {scanProgress && (
              <div className="p-4 bg-zinc-800/70 rounded-lg border border-zinc-700 animate-pulse">
                <div className="flex items-center gap-3">
                  <Loader2 className="w-5 h-5 animate-spin text-primary" />
                  <div className="flex-1">
                    <p className="text-white font-medium">{scanProgress.step}</p>
                    <div className="w-full bg-zinc-700 rounded-full h-2 mt-2">
                      <div 
                        className="bg-primary h-2 rounded-full transition-all duration-500"
                        style={{ width: `${(scanProgress.current / scanProgress.total) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
        
      case 3:
        return (
          <div className="space-y-6">
            <div className="p-4 bg-zinc-800/50 rounded-lg">
              <p className="text-white font-semibold mb-2">
                {allProducts.length} produits dans "{selectedTab?.name}"
              </p>
              <p className="text-sm text-gray-400">
                {allProducts.filter(p => p.image_url).length} avec images extraites des formules
              </p>
            </div>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-white">Filtrer par mot-clé</Label>
                <Input
                  placeholder='Ex: "Nike", "Jordan", "Ralph Lauren"'
                  value={filterKeyword}
                  onChange={(e) => setFilterKeyword(e.target.value)}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid="filter-keyword-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-white">Prix max (USD)</Label>
                  <Input
                    type="number"
                    placeholder="Ex: 50"
                    value={filterMaxPrice}
                    onChange={(e) => setFilterMaxPrice(e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white">Nombre max à importer</Label>
                  <Input
                    type="number"
                    value={filterLimit}
                    onChange={(e) => setFilterLimit(parseInt(e.target.value) || 200)}
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                  <p className="text-xs text-gray-500">Max 200 par import</p>
                </div>
              </div>
            </div>
            
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setCurrentStep(2)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour
              </Button>
              <Button 
                onClick={handleApplyFilters}
                className="flex-1 bg-primary"
                data-testid="apply-filters-btn"
              >
                <Filter className="w-4 h-4 mr-2" />
                Appliquer les filtres
              </Button>
            </div>
          </div>
        );
        
      case 4:
        return (
          <div className="space-y-4">
            {/* Header avec stats et actions */}
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <p className="text-white font-semibold">
                  {selectedProducts.size} / {filteredProducts.length} sélectionné(s)
                </p>
                <p className="text-xs text-gray-400">
                  {filteredProducts.filter(p => p.image_url).length} avec images
                </p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={toggleCurrentPage}>
                  {`Page ${previewPage + 1}`}
                </Button>
                <Button variant="outline" size="sm" onClick={toggleAll}>
                  {selectedProducts.size === filteredProducts.length ? 'Tout désélectionner' : 'Tout sélectionner'}
                </Button>
              </div>
            </div>
            
            {/* Pagination */}
            {totalPreviewPages > 1 && (
              <div className="flex items-center justify-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setPreviewPage(p => Math.max(0, p - 1))}
                  disabled={previewPage === 0}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm text-gray-400">
                  Page {previewPage + 1} / {totalPreviewPages}
                </span>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setPreviewPage(p => Math.min(totalPreviewPages - 1, p + 1))}
                  disabled={previewPage >= totalPreviewPages - 1}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}
            
            {/* Liste virtualisée */}
            <div 
              ref={parentRef}
              className="h-[400px] overflow-auto border border-zinc-700 rounded-lg"
              data-testid="product-list"
            >
              <div
                style={{
                  height: `${rowVirtualizer.getTotalSize()}px`,
                  width: '100%',
                  position: 'relative',
                }}
              >
                {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                  const product = paginatedProducts[virtualRow.index];
                  const globalIdx = previewPage * PREVIEW_PAGE_SIZE + virtualRow.index;
                  const isSelected = selectedProducts.has(globalIdx);
                  
                  return (
                    <div
                      key={virtualRow.index}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: `${virtualRow.size}px`,
                        transform: `translateY(${virtualRow.start}px)`,
                      }}
                      className="px-2"
                    >
                      <div 
                        className={`p-3 rounded-lg border flex items-center gap-3 h-[72px] ${
                          isSelected 
                            ? 'border-primary bg-primary/5' 
                            : 'border-zinc-700 bg-zinc-800/30'
                        }`}
                      >
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleProduct(globalIdx)}
                        />
                        
                        {product.image_url ? (
                          <img 
                            src={product.image_url} 
                            alt="" 
                            className="w-12 h-12 object-cover rounded"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling?.classList?.remove('hidden');
                            }}
                          />
                        ) : null}
                        {!product.image_url && (
                          <div className="w-12 h-12 bg-zinc-700 rounded flex items-center justify-center">
                            <ImageIcon className="w-6 h-6 text-zinc-500" />
                          </div>
                        )}
                        {product.image_url && (
                          <div className="w-12 h-12 bg-zinc-700 rounded flex items-center justify-center hidden">
                            <ImageIcon className="w-6 h-6 text-zinc-500" />
                          </div>
                        )}
                        
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm font-medium truncate">{product.name}</p>
                          <div className="flex items-center gap-3 mt-1">
                            <span className="text-xs text-gray-400">
                              ${product.price_usd || '?'} USD
                            </span>
                            <span className="text-xs text-green-400">
                              → ₪{calculateSellingPrice(product.price_cny, product.price_usd)}
                            </span>
                            {product.image_url && (
                              <Badge variant="secondary" className="text-[10px] py-0 px-1 bg-green-500/20 text-green-400">
                                IMG
                              </Badge>
                            )}
                          </div>
                        </div>
                        
                        {product.acbuy_url && !product.acbuy_url.includes('search?keyword=') && (
                          <a 
                            href={product.acbuy_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-primary text-xs hover:underline"
                          >
                            ACBuy
                          </a>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setCurrentStep(3)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour
              </Button>
              <Button 
                onClick={() => setCurrentStep(5)}
                disabled={selectedProducts.size === 0}
                className="flex-1 bg-primary"
              >
                <ArrowRight className="w-4 h-4 mr-2" />
                Continuer ({selectedProducts.size})
              </Button>
            </div>
          </div>
        );
        
      case 5:
        return (
          <div className="space-y-6">
            {!importResults && !importProgress ? (
              <>
                <div className="p-4 bg-zinc-800/50 rounded-lg">
                  <p className="text-white font-semibold">
                    {selectedProducts.size} produit(s) à importer
                  </p>
                  <p className="text-sm text-gray-400">
                    {filteredProducts.filter((_, idx) => selectedProducts.has(idx) && filteredProducts[idx]?.image_url).length} avec images
                  </p>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-zinc-800/30 rounded-lg">
                    <div>
                      <p className="text-white text-sm font-medium">Génération IA</p>
                      <p className="text-xs text-gray-400">Description générée par Claude</p>
                    </div>
                    <Checkbox
                      checked={importOptions.generate_ai}
                      onCheckedChange={(checked) => setImportOptions({...importOptions, generate_ai: checked})}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-zinc-800/30 rounded-lg">
                    <div>
                      <p className="text-white text-sm font-medium">Importer en brouillon</p>
                      <p className="text-xs text-gray-400">Revoir avant publication</p>
                    </div>
                    <Checkbox
                      checked={importOptions.as_draft}
                      onCheckedChange={(checked) => setImportOptions({...importOptions, as_draft: checked})}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-white">Multiplicateur de marge</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={importOptions.margin_rule}
                      onChange={(e) => setImportOptions({...importOptions, margin_rule: parseFloat(e.target.value) || 2.5})}
                      className="bg-zinc-800 border-zinc-700 text-white"
                    />
                  </div>
                </div>
                
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => setCurrentStep(4)}>
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Retour
                  </Button>
                  <Button 
                    onClick={handleImport}
                    disabled={loading}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    data-testid="import-btn"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
                    Lancer l'import
                  </Button>
                </div>
              </>
            ) : importProgress && !importResults ? (
              // Progression en temps réel
              <div className="space-y-6">
                <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {importProgress.status === 'paused' ? (
                        <Pause className="w-4 h-4 text-yellow-400" />
                      ) : (
                        <Loader2 className="w-4 h-4 animate-spin text-blue-400" />
                      )}
                      <span className={importProgress.status === 'paused' ? 'text-yellow-300' : 'text-blue-300'}>
                        {importProgress.status === 'paused' 
                          ? '⏸️ Import en pause' 
                          : importProgress.status === 'cancelled'
                            ? '🛑 Import annulé'
                            : 'Import en cours...'}
                      </span>
                    </div>
                    
                    {/* Boutons de contrôle Pause/Resume/Cancel */}
                    {importProgress.status !== 'cancelled' && importProgress.status !== 'completed' && (
                      <div className="flex gap-2">
                        {importProgress.status === 'paused' ? (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={handleResumeImport}
                            className="border-green-500/50 text-green-400 hover:bg-green-500/20"
                          >
                            <Play className="w-4 h-4 mr-1" />
                            Reprendre
                          </Button>
                        ) : (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={handlePauseImport}
                            className="border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/20"
                          >
                            <Pause className="w-4 h-4 mr-1" />
                            Pause
                          </Button>
                        )}
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={handleCancelImport}
                          className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Annuler
                        </Button>
                      </div>
                    )}
                  </div>
                  
                  <div className="w-full bg-zinc-700 rounded-full h-3 mb-2">
                    <div 
                      className={`h-3 rounded-full transition-all duration-300 ${
                        importProgress.status === 'paused' ? 'bg-yellow-500' :
                        importProgress.status === 'cancelled' ? 'bg-red-500' :
                        'bg-blue-500'
                      }`}
                      style={{ width: `${(importProgress.processed / importProgress.total) * 100}%` }}
                    />
                  </div>
                  
                  <div className="flex justify-between text-sm text-gray-400">
                    <span>{importProgress.processed} / {importProgress.total}</span>
                    <span>{Math.round((importProgress.processed / importProgress.total) * 100)}%</span>
                  </div>
                  
                  {importProgress.status === 'paused' && (
                    <p className="text-xs text-yellow-400 mt-2">
                      ⏸️ Import mis en pause — {importProgress.processed}/{importProgress.total} produits traités
                    </p>
                  )}
                  
                  {importProgress.status === 'cancelled' && (
                    <p className="text-xs text-red-400 mt-2">
                      🛑 Import annulé — {importProgress.processed} produits ont été importés avant l'annulation
                    </p>
                  )}
                  
                  {importProgress.current_product && importProgress.status !== 'paused' && importProgress.status !== 'cancelled' && (
                    <p className="text-xs text-gray-500 mt-2 truncate">
                      En cours: {importProgress.current_product}
                    </p>
                  )}
                </div>
                
                {/* Stats en temps réel */}
                <div className="grid grid-cols-3 gap-2">
                  <div className="p-2 bg-green-500/10 border border-green-500/30 rounded text-center">
                    <p className="text-lg font-bold text-green-400">{importProgress.imported}</p>
                    <p className="text-xs text-gray-400">Importés</p>
                  </div>
                  <div className="p-2 bg-yellow-500/10 border border-yellow-500/30 rounded text-center">
                    <p className="text-lg font-bold text-yellow-400">{importProgress.duplicates}</p>
                    <p className="text-xs text-gray-400">Doublons</p>
                  </div>
                  <div className="p-2 bg-red-500/10 border border-red-500/30 rounded text-center">
                    <p className="text-lg font-bold text-red-400">{importProgress.errors}</p>
                    <p className="text-xs text-gray-400">Erreurs</p>
                  </div>
                </div>
                
                {/* Logs en temps réel avec étapes détaillées */}
                <div className="max-h-[250px] overflow-y-auto space-y-2 bg-zinc-900 rounded-lg p-2">
                  {importProgress.logs?.slice(-8).map((log, idx) => (
                    <div 
                      key={idx}
                      className={`p-3 rounded text-xs ${
                        log.type === 'imported' ? 'bg-green-500/10 border border-green-500/20' :
                        log.type === 'duplicate' ? 'bg-yellow-500/10 border border-yellow-500/20' :
                        'bg-red-500/10 border border-red-500/20'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        {log.type === 'imported' ? <CheckCircle className="w-4 h-4 text-green-400" /> :
                         log.type === 'duplicate' ? <AlertCircle className="w-4 h-4 text-yellow-400" /> :
                         <XCircle className="w-4 h-4 text-red-400" />}
                        <span className="truncate flex-1 font-medium text-white">{log.name}</span>
                        {log.image_source && (
                          <Badge variant="secondary" className={`text-[10px] py-0 px-1 ${
                            log.image_source === 'requests' ? 'bg-blue-500/20 text-blue-400' :
                            log.image_source === 'unsplash' ? 'bg-purple-500/20 text-purple-400' :
                            'bg-gray-500/20 text-gray-400'
                          }`}>
                            {log.image_source === 'requests' ? '📸 ACBUY' :
                             log.image_source === 'unsplash' ? '🖼️ Unsplash' :
                             '❌ No IMG'}
                          </Badge>
                        )}
                        {log.variants_count > 0 && (
                          <Badge variant="secondary" className="text-[10px] py-0 px-1 bg-cyan-500/20 text-cyan-400">
                            {log.variants_count} tailles
                          </Badge>
                        )}
                      </div>
                      {log.steps && log.steps.length > 0 && (
                        <div className="ml-6 text-[10px] text-gray-400 space-y-0.5">
                          {log.steps.map((step, stepIdx) => (
                            <div key={stepIdx}>{step}</div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              // Résultats finaux avec rapport détaillé
              <div className="space-y-6">
                {/* Stats principales */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg text-center">
                    <CheckCircle className="w-8 h-8 text-green-400 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-green-400">{importResults.imported}</p>
                    <p className="text-xs text-gray-400">Fiches complètes</p>
                  </div>
                  <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg text-center">
                    <AlertCircle className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-yellow-400">{importResults.duplicates}</p>
                    <p className="text-xs text-gray-400">Doublons</p>
                  </div>
                  <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-center">
                    <XCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-red-400">{importResults.errors}</p>
                    <p className="text-xs text-gray-400">Erreurs</p>
                  </div>
                </div>
                
                {/* Rapport des images */}
                {importResults.stats && (
                  <div className="p-4 bg-zinc-800/50 rounded-lg">
                    <p className="text-white font-semibold mb-2">📸 Rapport des images</p>
                    <div className="grid grid-cols-2 gap-2 text-center text-sm">
                      <div className="p-2 bg-green-500/10 rounded">
                        <p className="text-green-400 font-bold">{importResults.stats.with_real_photos || 0}</p>
                        <p className="text-xs text-gray-400">Photos capturées</p>
                      </div>
                      <div className="p-2 bg-amber-500/10 rounded">
                        <p className="text-amber-400 font-bold">{importResults.stats.without_photos || 0}</p>
                        <p className="text-xs text-gray-400">⚠️ Photos manquantes</p>
                      </div>
                    </div>
                    {importResults.stats.without_photos > 0 && (
                      <p className="text-xs text-amber-400/70 mt-2 text-center">
                        Les produits sans photos ont été créés en brouillon. 
                        Vous pouvez capturer les photos plus tard depuis la fiche produit.
                      </p>
                    )}
                  </div>
                )}
                
                {/* Liste détaillée des produits */}
                <div className="max-h-[300px] overflow-y-auto space-y-2">
                  {importResults.details?.map((detail, idx) => (
                    <div 
                      key={idx}
                      className={`p-3 rounded text-sm border ${
                        detail.type === 'imported' ? 'bg-green-500/5 border-green-500/20 text-green-300' :
                        detail.type === 'duplicate' ? 'bg-yellow-500/5 border-yellow-500/20 text-yellow-300' :
                        'bg-red-500/5 border-red-500/20 text-red-300'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {detail.type === 'imported' ? <CheckCircle className="w-4 h-4 flex-shrink-0" /> :
                         detail.type === 'duplicate' ? <AlertCircle className="w-4 h-4 flex-shrink-0" /> :
                         <XCircle className="w-4 h-4 flex-shrink-0" />}
                        <span className="truncate flex-1 font-medium">{detail.name}</span>
                        
                        {detail.photo_source && (
                          <Badge variant="secondary" className={`text-[10px] py-0 px-1 flex-shrink-0 ${
                            detail.photo_source === 'acbuy' && detail.photo_count > 0 ? 'bg-green-500/20 text-green-400' :
                            'bg-amber-500/20 text-amber-400'
                          }`}>
                            {detail.photo_source === 'acbuy' && detail.photo_count > 0 ? `📸 ${detail.photo_count}` : '⚠️ 0'}
                          </Badge>
                        )}
                        
                        {detail.variants_count > 0 && (
                          <Badge variant="secondary" className="text-[10px] py-0 px-1 bg-cyan-500/20 text-cyan-400 flex-shrink-0">
                            {detail.variants_count} var.
                          </Badge>
                        )}
                        
                        {detail.selling_price && (
                          <span className="text-xs text-gray-400 flex-shrink-0">₪{detail.selling_price}</span>
                        )}
                      </div>
                      
                      {detail.steps && detail.steps.length > 0 && (
                        <div className="ml-6 mt-1 text-[10px] text-gray-500 space-y-0.5">
                          {detail.steps.map((step, stepIdx) => (
                            <div key={stepIdx}>{step}</div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                
                <div className="flex gap-3">
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setImportResults(null);
                      setImportProgress(null);
                      setImportJobId(null);
                      setCurrentStep(1);
                      setSpreadsheetUrl('');
                      setTabs([]);
                      setAllProducts([]);
                      setFilteredProducts([]);
                      setSelectedProducts(new Set());
                    }}
                  >
                    Nouvel import
                  </Button>
                  {importResults.stats?.needs_validation_count > 0 && (
                    <Button 
                      variant="outline"
                      onClick={() => setCurrentStep(6)}
                      className="border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/20"
                    >
                      <AlertCircle className="w-4 h-4 mr-2" />
                      Valider {importResults.stats.needs_validation_count} photo(s)
                    </Button>
                  )}
                  <Button 
                    onClick={() => navigate('/admin/sourcing')}
                    className="flex-1 bg-primary"
                  >
                    Voir le catalogue
                  </Button>
                </div>
              </div>
            )}
          </div>
        );
        
      case 6:
        // Étape 6: Validation des photos capturées
        return (
          <div className="space-y-6">
            {!importResults?.needs_validation_products?.length ? (
              <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg text-center">
                <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
                <p className="text-green-300 font-semibold">Toutes les photos ont été capturées correctement !</p>
                <p className="text-sm text-gray-400 mt-1">
                  Les images proviennent de la galerie principale de chaque produit.
                </p>
              </div>
            ) : (
              <>
                <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                  <p className="text-yellow-300 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5" />
                    <strong>{importResults.needs_validation_products.length} produit(s)</strong> nécessitent une validation
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Les photos peuvent ne pas correspondre au bon produit. Vérifiez et corrigez si nécessaire.
                  </p>
                </div>
                
                {importResults.needs_validation_products.map((product, idx) => (
                  <div key={product.id} className="p-4 bg-zinc-800/50 rounded-lg border border-zinc-700">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="flex-1">
                        <p className="text-white font-semibold">{product.name}</p>
                        <p className="text-xs text-gray-400">ID: {product.id}</p>
                      </div>
                      <Badge variant="secondary" className="bg-yellow-500/20 text-yellow-400">
                        ⚠️ À vérifier
                      </Badge>
                    </div>
                    
                    {/* Aperçu des photos capturées */}
                    <div className="mb-4">
                      <p className="text-sm text-gray-300 mb-2">Photos capturées :</p>
                      <div className="grid grid-cols-4 gap-2">
                        {product.images?.slice(0, 4).map((img, imgIdx) => (
                          <div key={imgIdx} className="aspect-square bg-zinc-700 rounded overflow-hidden relative">
                            <img 
                              src={`${API_URL}${img}`} 
                              alt={`Photo ${imgIdx + 1}`}
                              className="w-full h-full object-cover"
                              onError={(e) => e.target.src = '/placeholder.png'}
                            />
                            {product.metadata?.[imgIdx] && (
                              <span className="absolute bottom-0 left-0 right-0 bg-black/70 text-[9px] text-gray-300 px-1 truncate">
                                ({product.metadata[imgIdx].position?.x}, {product.metadata[imgIdx].position?.y})
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <p className="text-sm text-gray-300 mb-3">Ces photos correspondent-elles au bon produit ?</p>
                    
                    {/* Boutons d'action */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      <Button
                        size="sm"
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => handleValidatePhotos(product.id)}
                        disabled={validationLoading}
                      >
                        ✅ Oui, continuer
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-blue-500/50 text-blue-400 hover:bg-blue-500/20"
                        onClick={() => handleRecapturePhotos(product.id)}
                        disabled={validationLoading}
                      >
                        {validationLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : '🔄'} Recapturer
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/20"
                        onClick={() => setPhotoValidation(prev => ({ ...prev, showCustomInput: product.id }))}
                      >
                        ✏️ Mes propres URLs
                      </Button>
                    </div>
                    
                    {/* Champ pour URLs personnalisées */}
                    {photoValidation.showCustomInput === product.id && (
                      <div className="p-3 bg-zinc-900 rounded-lg space-y-2">
                        <Label className="text-xs text-gray-400">
                          Collez 3-5 URLs d'images (une par ligne) :
                        </Label>
                        <textarea
                          className="w-full h-24 bg-zinc-800 border border-zinc-700 rounded p-2 text-sm text-white"
                          placeholder="https://exemple.com/image1.jpg&#10;https://exemple.com/image2.jpg"
                          value={photoValidation.customUrls}
                          onChange={(e) => setPhotoValidation(prev => ({ ...prev, customUrls: e.target.value }))}
                        />
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleAddCustomUrls(product.id)}
                            disabled={validationLoading}
                          >
                            {validationLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                            Télécharger et remplacer
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setPhotoValidation(prev => ({ ...prev, showCustomInput: null, customUrls: '' }))}
                          >
                            Annuler
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </>
            )}
            
            <div className="flex gap-3 pt-4">
              <Button 
                variant="outline" 
                onClick={() => setCurrentStep(5)}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour aux résultats
              </Button>
              <Button 
                onClick={() => navigate('/admin/sourcing')}
                className="flex-1 bg-primary"
              >
                Terminer et voir le catalogue
              </Button>
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/sourcing')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-white">Import Spreadsheet</h1>
            <p className="text-gray-400">Importer depuis Google Spreadsheet ACBUY</p>
          </div>
        </div>
        
        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {STEPS.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${
                    currentStep > step.id 
                      ? 'bg-green-500 border-green-500 text-white' 
                      : currentStep === step.id 
                        ? 'bg-primary border-primary text-white' 
                        : 'bg-zinc-800 border-zinc-700 text-gray-500'
                  }`}>
                    {currentStep > step.id ? (
                      <Check className="w-5 h-5" />
                    ) : (
                      <step.icon className="w-5 h-5" />
                    )}
                  </div>
                  <span className={`text-xs mt-2 ${currentStep === step.id ? 'text-primary' : 'text-gray-500'}`}>
                    {step.name}
                  </span>
                </div>
                {index < STEPS.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-2 ${currentStep > step.id ? 'bg-green-500' : 'bg-zinc-700'}`} />
                )}
              </div>
            ))}
          </div>
        </div>
        
        {/* Step Content */}
        <Card className="bg-zinc-900/60 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              {STEPS[currentStep - 1] && (() => {
                const StepIcon = STEPS[currentStep - 1].icon;
                return <StepIcon className="w-5 h-5 text-primary" />;
              })()}
              Étape {currentStep}: {STEPS[currentStep - 1]?.name}
            </CardTitle>
            <CardDescription>{STEPS[currentStep - 1]?.description}</CardDescription>
          </CardHeader>
          <CardContent>
            {renderStepContent()}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}

export default AdminSpreadsheetImportPage;
